import type { TExample } from '@atlas/uicorn/preview/modules';
import React, { useImperativeHandle } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Tooltip as AntTooltip, TooltipProps as TAntTooltipProps } from 'antd';
import useMemoState from '@atlas/tool/box/hooks/useMemoState';
import useRefCallback from 'use-ref-callback';
import { registerStylesheet } from '@atlas/uicorn/lib/System/StylesProvider';
import { RELATIVE_TIME_TOOLTIP_CLASS_NAME } from '@atlas/uicorn/lib/Data/RelativeTime';

const Tooltip = React.memo(
    React.forwardRef(function Tooltip(props: TTooltipProps, ref: React.ForwardedRef<TTooltipRef>): React.ReactElement {
        const {
            title,
            placement = 'top',
            open: propsOpen,
            zIndex = 100,
            onOpenChange,
            children,
            className,
            whenOverflowed,
        } = props;
        const [open, setOpen] = useMemoState<boolean>(
            (previous) => {
                if (typeof propsOpen === 'boolean') return propsOpen;
                return previous ?? false;
            },
            [propsOpen],
        );

        useImperativeHandle(ref, () => ({
            open: () => setOpen(true),
            close: () => setOpen(false),
        }));

        const handleOpenChange = useRefCallback((visibility) => {
            if (typeof propsOpen === 'undefined') setOpen(visibility);
            onOpenChange?.(visibility);
        });

        const getPopupContainer = useRefCallback((trigger: HTMLElement): HTMLElement => {
            // Returning null doesn't crash it but prevents the tooltip from being rendered
            if (trigger.dataset.tooltipDisabled === 'true') return document.createElement('i');

            if (whenOverflowed) {
                const el = typeof whenOverflowed === 'function' ? whenOverflowed(trigger) : trigger;
                if (el.scrollWidth <= el.clientWidth && el.scrollHeight <= el.clientHeight) {
                    return document.createElement('i');
                }
            }

            return trigger.closest('dialog') || document.body;
        });

        return (
            <STooltip
                className={className}
                getPopupContainer={getPopupContainer}
                title={title}
                placement={placement}
                destroyTooltipOnHide
                open={open}
                onOpenChange={handleOpenChange}
                showArrow={false}
                zIndex={zIndex}
                children={children}
            />
        );
    }),
);

export default Tooltip;

// #region Styles

const STooltip = styled(({ className, ...tooltipProps }: Omit<TAntTooltipProps, 'overlayClassName'>) => (
    <AntTooltip mouseLeaveDelay={0} {...tooltipProps} overlayClassName={className} zIndex={1060} />
))`
    // TODO: Introduce the component that will render OS specific shortcut from Hotkey instance
    kbd {
        white-space: nowrap;
        background-color: ${(p) => p.theme.color.grey61};
        color: ${(p) => p.theme.color.grey9};
        border-radius: ${(p) => p.theme.radius.small};
        padding: 0.25rem 0.4rem;
        font: ${(p) => p.theme.font.caption};

        // Non Apple key styles
        letter-spacing: -0.5px;
        small {
            font: inherit;
            display: inline-block;
            scale: 0.9 1;
            margin-left: -1px;
            margin-right: -1px;
        }
    }
`;

const STooltipGlobalStyle = createGlobalStyle`
    // Not used in STooltip so that all Ant tooltips are styled

    // Remove spacing for arrow
    .ant-tooltip {
        padding: 0;
        text-align: center;
    }

    .ant-tooltip.${RELATIVE_TIME_TOOLTIP_CLASS_NAME} {
        max-width: unset;
    }

    .ant-tooltip-inner {
        border-radius: 0.6rem;
        color: ${(p) => p.theme.color.grey00};
        background-color: ${(p) => p.theme.color.grey91};
        font: ${(p) => p.theme.font.tooltip};
        box-shadow: ${(p) => p.theme.shadow.extraLow};
        text-align: inherit;
        padding: 0.4rem 0.8rem;
        min-height: auto;

        &:empty {
            opacity: 0;
        }
    }

    .ant-tooltip-arrow {
        display:  none;
    }
`;

registerStylesheet(STooltipGlobalStyle);

// #endregion

// #region Types

export type TTooltipRef = {
    open(): void;
    close(): void;
};

export type TTooltipProps = Pick<
    TAntTooltipProps,
    | 'title'
    | 'className'
    // Properties common with Popover
    // | 'align' // Use placement instead
    // | 'autoAdjustOverflow' // Let it always adjust if tooltip is open outside of viewport
    // | 'color' // Hide until we don't have customizable tooltips
    // | 'defaultOpen'
    // | 'destroyTooltipOnHide'
    // | 'getPopupContainer' // Can be useful for dialog modals
    // | 'mouseEnterDelay' // Should be standard across all tooltips
    // | 'mouseLeaveDelay' // Should be standard across all tooltips
    // | 'overlayClassName' // Use className instead
    // | 'overlayStyle' // Use styled-components instead
    // | 'overlayInnerStyle' // Use styled-components instead
    | 'placement'
    // | 'trigger'
    | 'open'
    | 'zIndex'
    | 'onOpenChange'
> & {
    children?: React.ReactElement | null;
    whenOverflowed?: boolean | ((trigger: HTMLElement) => HTMLElement);
};

// #endregion

// #region Examples

export const Example: TExample = {
    render({ withShortcut, ...props }) {
        return (
            <Tooltip
                {...props}
                title={
                    withShortcut ? (
                        <>
                            Refresh page <kbd>⌘R</kbd>
                        </>
                    ) : (
                        "This button doesn't do anything"
                    )
                }
            >
                <button type="button" children="button" />
            </Tooltip>
        );
    },
    controls: {
        open: { type: 'boolean', initial: true, connected: false },
        placement: [
            'top',
            'left',
            'right',
            'bottom',
            'topLeft',
            'topRight',
            'bottomLeft',
            'bottomRight',
            'leftTop',
            'leftBottom',
            'rightTop',
            'rightBottom',
        ],
        withShortcut: 'boolean',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?node-id=25%3A3870&mode=dev',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('Tooltip', () => {
        it.todo('🤷🏼');
    });
}

// #endregion
